import { useEffect, useState, type FormEvent } from "react";
import {
  ApiError,
  addColleague,
  fetchContacts,
  fetchParticipants,
  inviteParticipant,
  resendInvitation,
  revokeParticipant,
  type ContactHit,
  type Participant,
  type ParticipantList,
} from "./api.js";
import styles from "./Live.module.css";
import ui from "./Ui.module.css";
import { Modal } from "./ui/Modal.js";

/**
 * The Share panel (brief 8.1), for the map's owner and staff editors: invite client participants
 * by email as Editor or Viewer, see who has access, resend an invitation, remove access. Contacts on
 * the map's Account pre-fill an invitation. The owner can also add a SalesFix colleague as an
 * editor (decided by Jason, 16 October 2026). Staff only: it shows email addresses.
 */

const ROLE = { owner: "Owner", editor: "Editor", viewer: "Viewer" } as const;

export function SharePanel({
  mapId,
  onClose,
  onMessage,
}: {
  mapId: string;
  onClose: () => void;
  /** A short confirmation for the page's toast. */
  onMessage: (text: string) => void;
}) {
  const [list, setList] = useState<ParticipantList | null | "failed">(null);
  const [contacts, setContacts] = useState<ContactHit[]>([]);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState<"editor" | "viewer">("editor");
  const [contactId, setContactId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);
  const [colleague, setColleague] = useState("");
  const [colleagueError, setColleagueError] = useState<string | null>(null);
  const [version, setVersion] = useState(0);

  useEffect(() => {
    const ctl = new AbortController();
    fetchParticipants(mapId, ctl.signal).then(setList, (err: unknown) => {
      if ((err as Error).name !== "AbortError") setList("failed");
    });
    return () => ctl.abort();
  }, [mapId, version]);

  useEffect(() => {
    const ctl = new AbortController();
    fetchContacts(mapId, ctl.signal).then(setContacts, () => setContacts([]));
    return () => ctl.abort();
  }, [mapId]);

  const reload = () => setVersion((v) => v + 1);
  const canManage = list !== null && list !== "failed" && list.permissions.manage;
  const canAddColleague = list !== null && list !== "failed" && list.permissions.addColleague;
  const people = list && list !== "failed" ? list.participants : [];
  const shown = people.filter((p) => p.status !== "Revoked");
  const removed = people.filter((p) => p.status === "Revoked");

  async function act(task: () => Promise<void>, failure: string) {
    setError(null);
    setBusy(true);
    try {
      await task();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : failure);
    } finally {
      setBusy(false);
    }
  }

  function pickContact(id: string) {
    setContactId(id);
    const c = contacts.find((x) => x.id === id);
    if (c) {
      setEmail(c.email);
      setName(c.name);
    }
  }

  function invite(e: FormEvent) {
    e.preventDefault();
    void act(async () => {
      const r = await inviteParticipant(mapId, {
        email,
        name,
        role,
        contactId: contactId || undefined,
      });
      onMessage(
        r.emailed
          ? `Invitation sent to ${email.trim()}`
          : "Invitation saved, but the email could not be sent. Use Resend.",
      );
      setEmail("");
      setName("");
      setContactId("");
      setRole("editor");
      reload();
    }, "The invitation could not be sent.");
  }

  function addEditor(e: FormEvent) {
    e.preventDefault();
    setColleagueError(null);
    setBusy(true);
    addColleague(mapId, colleague.trim()).then(
      (r) => {
        onMessage(`${r.name} can now edit this value map`);
        setColleague("");
        setBusy(false);
        reload();
      },
      (err: unknown) => {
        setColleagueError(
          err instanceof ApiError ? err.message : "The colleague could not be added.",
        );
        setBusy(false);
      },
    );
  }

  function row(p: Participant) {
    const staffRow = p.type === "Internal";
    return (
      <li key={p.id} className={styles.person}>
        <div className={styles.personText}>
          <span className={styles.personName}>{p.name || p.email}</span>
          {staffRow && " (SalesFix)"}
          <span className={styles.personEmail}>{p.email}</span>
        </div>
        <span className={styles.tag}>{ROLE[p.role]}</span>
        <span className={`${styles.tag} ${p.status === "Revoked" ? styles.revoked : ""}`}>
          {p.status === "Revoked" ? "Access removed" : p.status}
        </span>
        {canManage && p.status !== "Revoked" && confirming !== p.id && (
          <>
            {!staffRow && (
              <button
                type="button"
                className={styles.smallBtn}
                disabled={busy}
                onClick={() =>
                  void act(async () => {
                    await resendInvitation(mapId, p.id);
                    onMessage(`Invitation sent again to ${p.email}`);
                  }, "The invitation could not be sent.")
                }
              >
                Resend
              </button>
            )}
            <button
              type="button"
              className={styles.smallBtn}
              disabled={busy}
              onClick={() => setConfirming(p.id)}
            >
              Remove access
            </button>
          </>
        )}
        {confirming === p.id && (
          <>
            <button
              type="button"
              className={`${styles.smallBtn} ${styles.primaryBtn}`}
              disabled={busy}
              aria-label={`Confirm removing access for ${p.name || p.email}`}
              onClick={() =>
                void act(async () => {
                  await revokeParticipant(mapId, p.id);
                  setConfirming(null);
                  onMessage(`Access removed for ${p.name || p.email}`);
                  reload();
                }, "Access could not be removed.")
              }
            >
              Remove now
            </button>
            <button type="button" className={styles.smallBtn} onClick={() => setConfirming(null)}>
              Keep
            </button>
          </>
        )}
      </li>
    );
  }

  return (
    <Modal title="Share this value map" onClose={onClose} wide>
      <div className={styles.share}>
        {list === null && <p>Loading…</p>}
        {list === "failed" && (
          <p className={styles.error} role="alert">
            The people on this value map could not be loaded.
          </p>
        )}
        {list && list !== "failed" && (
          <>
            {shown.length === 0 ? (
              <p>Nobody has been invited yet.</p>
            ) : (
              <ul className={styles.people} aria-label="People with access">
                {shown.map(row)}
              </ul>
            )}
            {removed.length > 0 && (
              <details>
                <summary className={styles.hint}>Access removed ({removed.length})</summary>
                <ul className={styles.people}>{removed.map(row)}</ul>
              </details>
            )}
          </>
        )}

        {canAddColleague && (
          <form onSubmit={addEditor} noValidate>
            <h4>Add a SalesFix colleague as an editor</h4>
            <div className={styles.inviteGrid}>
              <div className={styles.wide}>
                <label htmlFor="share-colleague">Their SalesFix email address</label>
                <input
                  id="share-colleague"
                  type="email"
                  value={colleague}
                  maxLength={254}
                  autoComplete="off"
                  onChange={(e) => setColleague(e.target.value)}
                />
              </div>
            </div>
            {colleagueError && (
              <p className={styles.error} role="alert">
                {colleagueError}
              </p>
            )}
            <div className={ui.row}>
              <button
                type="submit"
                className={`${ui.btn} ${ui.primary}`}
                disabled={busy || !colleague.trim()}
              >
                Add as editor
              </button>
            </div>
          </form>
        )}

        {canManage && (
          <form onSubmit={invite} noValidate>
            <h4>Invite someone from the client</h4>
            <div className={styles.inviteGrid}>
              {contacts.length > 0 && (
                <div className={styles.wide}>
                  <label htmlFor="share-contact">From the Account&apos;s Contacts</label>
                  <select
                    id="share-contact"
                    value={contactId}
                    onChange={(e) => pickContact(e.target.value)}
                  >
                    <option value="">Choose a Contact, or type below</option>
                    {contacts.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name} ({c.email})
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <label htmlFor="share-name">Name</label>
                <input
                  id="share-name"
                  value={name}
                  maxLength={120}
                  autoComplete="off"
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="share-email">Email</label>
                <input
                  id="share-email"
                  type="email"
                  value={email}
                  maxLength={254}
                  autoComplete="off"
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setContactId("");
                  }}
                />
              </div>
              <div>
                <label htmlFor="share-role">Role</label>
                <select
                  id="share-role"
                  value={role}
                  onChange={(e) => setRole(e.target.value as "editor" | "viewer")}
                >
                  <option value="editor">Editor: can add and change content</option>
                  <option value="viewer">Viewer: can look but not change</option>
                </select>
              </div>
            </div>
            <p className={styles.hint}>
              They will get an email from SalesFix with a link to this value map, and a sign-in code
              when they open it. Replies go to the map&apos;s owner. SalesFix colleagues do not need
              an invitation: they can open any value map and add themselves as an editor, or the
              map&apos;s owner can add them.
            </p>
            {error && (
              <p className={styles.error} role="alert">
                {error}
              </p>
            )}
            <div className={ui.row}>
              <button type="button" className={ui.btn} onClick={onClose}>
                Close
              </button>
              <button
                type="submit"
                className={`${ui.btn} ${ui.primary}`}
                disabled={busy || !email.trim() || !name.trim()}
              >
                Send invitation
              </button>
            </div>
          </form>
        )}

        {!canManage && (
          <>
            {error && (
              <p className={styles.error} role="alert">
                {error}
              </p>
            )}
            <div className={ui.row}>
              <button type="button" className={ui.btn} onClick={onClose}>
                Close
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
